import { z } from "zod";
import { CODIGOS_PAIS_WEBCHAT, COLUMNAS_TEMA, ESTADOS_WEBCHAT, FAMILIAS_PERMITIDAS, ICONOS_WEBCHAT, NAMEBOT_CLAVES, RANURAS_SUBIBLES, RE_ICONO_TRAZO, TEMA_COLUMNAS, ZONA_WEBCHAT, problemasDeContraste, } from "../webchat-catalogos.js";
// ---------------------------------------------------------------------------
// Módulo «Webchats»: validación de lo que llega al API. El servidor decide;
// la web usa los mismos schemas sólo para avisar antes. Los topes y patrones
// son los CHECK de socialmanager.WEBCHAT_EXT_* (leídos en bots-read el 14/10).
// ---------------------------------------------------------------------------
/* ── Texto plano ──────────────────────────────────────────────────────── */
function esControl(codigo, saltos) {
    if (saltos && codigo === 0x0a)
        return false;
    return codigo < 0x20 || codigo === 0x7f || (codigo >= 0x80 && codigo < 0xa0) || codigo === 0x2028 || codigo === 0x2029;
}
function tieneControles(s, saltos = false) {
    for (const ch of s) {
        if (esControl(ch.codePointAt(0) ?? 0, saltos))
            return true;
    }
    return false;
}
/** Caracteres fuera del plano básico (emojis): CANAL es utf8mb3 y no los guarda. */
export function tieneCuatroBytes(s) {
    for (const ch of s) {
        if ((ch.codePointAt(0) ?? 0) > 0xffff)
            return true;
    }
    return false;
}
export function tieneUrl(s) {
    const l = s.toLowerCase();
    return l.includes("http://") || l.includes("https://") || l.includes("www.");
}
/** Texto plano: sin marcado, sin controles y sin direcciones web. */
export function textoPlano(max, opciones = {}) {
    return z
        .string()
        .trim()
        .min(opciones.min ?? 1, "Obligatorio")
        .max(max, `Hasta ${max} caracteres`)
        .refine((s) => !tieneControles(s, opciones.saltos), "Sin caracteres de control")
        .refine((s) => !s.includes("<") && !s.includes(">"), "Sin < ni >")
        .refine((s) => !tieneUrl(s), "Sin direcciones web")
        .refine((s) => !opciones.utf8mb3 || !tieneCuatroBytes(s), "Sin emojis ni símbolos especiales")
        .refine((s) => !opciones.sinComillas || !s.includes('"'), "Sin comillas dobles");
}
/* ── Etiqueta y host ──────────────────────────────────────────────────── */
/** Etiqueta de un solo nivel, de 3 a 32 caracteres. */
export const RE_ETIQUETA = /^[a-z0-9](?:[a-z0-9-]{1,30}[a-z0-9])$/;
/** Sin «--» en las posiciones 3 y 4: es la marca de los nombres punycode (xn--). */
export function etiquetaValida(etiqueta) {
    return RE_ETIQUETA.test(etiqueta) && etiqueta.slice(2, 4) !== "--";
}
export const etiquetaSchema = z
    .string()
    .trim()
    .toLowerCase()
    .refine(etiquetaValida, "De 3 a 32 caracteres: minúsculas, dígitos y guiones; sin guion al borde ni «--» en la 3.ª y 4.ª posición");
export function hostDeEtiqueta(etiqueta) {
    return `${etiqueta}.${ZONA_WEBCHAT}`;
}
/** La etiqueta de un host de la zona, o null si no es de un solo nivel. */
export function etiquetaDeHost(host) {
    const sufijo = `.${ZONA_WEBCHAT}`;
    const h = host.toLowerCase();
    if (!h.endsWith(sufijo))
        return null;
    const etiqueta = h.slice(0, -sufijo.length);
    return etiqueta && !etiqueta.includes(".") ? etiqueta : null;
}
/* ── Valores sueltos ──────────────────────────────────────────────────── */
/** #RRGGBB; se guarda en mayúsculas. */
export const colorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9A-Fa-f]{6}$/, "Color #RRGGBB")
    .transform((c) => c.toUpperCase());
const RE_LABEL_HOST = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;
const RE_URL_PROHIBIDOS = /[\s"'<>\\`]/;
function hostDeNombreValido(host) {
    const labels = host.split(".");
    if (labels.length < 2 || !labels.every((l) => RE_LABEL_HOST.test(l)))
        return false;
    // Un host de nombre, nunca una IP: el último nivel no es numérico.
    return !/^\d+$/.test(labels.at(-1) ?? "");
}
/** https, host de nombre (no IP), sin credenciales ni puerto, hasta 300 caracteres. */
export function urlHttpsValida(s) {
    if (s.length > 300 || RE_URL_PROHIBIDOS.test(s) || !s.startsWith("https://"))
        return false;
    const resto = s.slice("https://".length);
    const fin = [...resto].findIndex((c) => c === "/" || c === "?" || c === "#");
    const autoridad = fin === -1 ? resto : resto.slice(0, fin);
    if (!autoridad || autoridad.includes("@") || autoridad.includes(":"))
        return false;
    return hostDeNombreValido(autoridad.toLowerCase());
}
export const urlHttpsSchema = z.string().trim().refine(urlHttpsValida, "Dirección https con nombre de host, hasta 300 caracteres");
export const paisSchema = z.enum(CODIGOS_PAIS_WEBCHAT);
/** El mismo patrón que CK_WEXC_ZONA. */
export const RE_ZONA_IANA = /^[A-Za-z_]+(?:\/[A-Za-z0-9_+-]+){1,2}$/;
let zonasCache = null;
/** Zonas IANA que conoce el motor de JavaScript y que acepta la base. */
export function zonasIana() {
    return Intl.supportedValuesOf("timeZone").filter((z) => RE_ZONA_IANA.test(z));
}
function esZonaIana(z) {
    zonasCache ??= new Set(zonasIana());
    return zonasCache.has(z);
}
export const zonaIanaSchema = z.string().trim().refine(esZonaIana, "Zona horaria IANA (America/Guatemala)");
export const idcanalSchema = z.coerce.number().int().positive().max(2_147_483_647);
/* ── OTP e identidad ──────────────────────────────────────────────────── */
/** El languageCode que acepta el webchat (lib/otp.js) y la base. */
export const RE_IDIOMA_OTP = /^[a-z]{2}(?:_[A-Z]{2})?$/;
export const otpSchema = z.object({
    /** IDCANAL del WhatsApp emisor (TIPO 7) en Template Builder. */
    idcanalEmisor: idcanalSchema,
    /** Nombre de la plantilla AUTHENTICATION aprobada en ese emisor. */
    plantilla: z.string().trim().regex(/^[a-z0-9_]{1,64}$/, "Minúsculas, dígitos y guion bajo"),
    idioma: z
        .string()
        .trim()
        .regex(RE_IDIOMA_OTP, "Código de idioma de la plantilla (es, es_MX)")
        .default("es"),
    digitos: z.number().int().min(4).max(8).default(4),
    vigenciaSeg: z.number().int().min(60).max(3600).default(600),
});
/** La plantilla que el Backoffice le crea a un WhatsApp: la copia de la que funciona en el emisor de demo. */
export const PLANTILLA_OTP_ESTANDAR = { nombre: "otp_verificacion", idioma: "es", digitos: 4, vigenciaSeg: 600 };
export const identidadEnum = z.enum(["otp", "token", "otp_o_token"]);
/** 'otp' y 'otp_o_token' exigen OTP; sin OTP sólo vale 'token'. */
export function identidadCoherente(identidad, otp) {
    return identidad === "token" || Boolean(otp);
}
/* ── Link (LINK_*) ────────────────────────────────────────────────────── */
const MARCADORES_LINK = ["{nombre}", "{url}"];
/** Sólo {nombre} y {url} como marcadores (CK_WEXC_LTEXTO). */
export function marcadoresLinkValidos(texto) {
    let resto = texto;
    for (const m of MARCADORES_LINK)
        resto = resto.replaceAll(m, "");
    return !resto.includes("{") && !resto.includes("}");
}
export const linkTextoSchema = textoPlano(900, { saltos: true, sinComillas: true }).refine(marcadoresLinkValidos, "Sólo los marcadores {nombre} y {url}");
export const linkSchema = z
    .object({
    envio: z.enum(["no", "sesion", "texto", "boton"]),
    texto: linkTextoSchema.nullable().default(null),
    boton: textoPlano(20, { sinComillas: true }).nullable().default(null),
    pie: textoPlano(60, { sinComillas: true }).nullable().default(null),
    accion: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .refine((s) => !tieneControles(s), "Sin caracteres de control")
        .nullable()
        .default(null),
    urlEnCuerpo: z.boolean().default(true),
    /** IDCANAL de WhatsApp (TIPO 7) de la misma company que pueden pedir el link. */
    origenes: z
        .array(idcanalSchema)
        .max(20)
        .default([])
        .refine((a) => new Set(a).size === a.length, "Sin repetidos"),
})
    .superRefine((l, ctx) => {
    if (l.envio !== "no" && (!l.texto || !l.boton)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["texto"], message: "Para enviar el link hacen falta el texto y el botón" });
    }
});
/* ── Países ───────────────────────────────────────────────────────────── */
const paisesPermitidosSchema = z
    .array(paisSchema)
    .min(1)
    .max(10)
    .refine((a) => new Set(a).size === a.length, "Sin repetidos");
function paisesCoherentes(defecto, permitidos) {
    return permitidos.includes(defecto);
}
/* ── Edición por secciones ────────────────────────────────────────────── */
/** Token de concurrencia: FECHAMODIFICACION como 'YYYY-MM-DD HH:MM:SS.ffffff'. */
export const versionSchema = z.string().regex(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}\.\d{6}$/, "Versión inválida");
export const webchatFichaPatchSchema = z
    .object({
    version: versionSchema,
    alias: textoPlano(80).nullable().optional(),
    idskillDefault: z.number().int().positive().optional(),
    identidad: identidadEnum.optional(),
    paisDefecto: paisSchema.optional(),
    paisesPermitidos: paisesPermitidosSchema.optional(),
    zonaIana: zonaIanaSchema.optional(),
    otp: otpSchema.nullable().optional(),
    link: linkSchema.optional(),
})
    .strict();
export const webchatBotPatchSchema = z
    .object({
    idbot: z.number().int().min(0).optional(),
    namebot: z.enum(NAMEBOT_CLAVES).optional(),
    activebot: z.boolean().optional(),
    confirmarBotNoVerificado: z.boolean().default(false),
})
    .strict()
    .refine((b) => b.idbot !== undefined || b.namebot !== undefined || b.activebot !== undefined, "Nada para cambiar");
/* ── Tema ─────────────────────────────────────────────────────────────── */
function validadorDe(def) {
    switch (def.tipo) {
        case "color":
            return colorSchema;
        case "texto":
            return textoPlano(def.max);
        case "enum":
            return z.enum(def.valores);
        case "bool":
            return z.union([z.literal(0), z.literal(1)]);
        case "entero":
            return z.number().int().min(def.min).max(def.max);
        case "decimal": {
            const factor = 10 ** def.decimales;
            return z
                .number()
                .min(def.min)
                .max(def.max)
                .transform((n) => Math.round(n * factor) / factor);
        }
        case "icono":
            return z.enum(ICONOS_WEBCHAT);
        case "fuente":
            return z.enum(FAMILIAS_PERMITIDAS);
        case "url":
            return urlHttpsSchema;
        case "idioma":
            return z.string().trim().regex(/^[a-z]{2}(?:-[A-Z]{2})?$/, "Idioma (es, es-GT)");
    }
}
const formaTema = Object.fromEntries(COLUMNAS_TEMA.map((col) => [col, validadorDe(TEMA_COLUMNAS[col]).nullable().optional()]));
/**
 * Los mismos validadores por columna, sin el control de contraste: para valores
 * que vienen de otro lado (una versión guardada que se quiere restaurar).
 */
export const webchatTemaValoresBaseSchema = z.object(formaTema).strict();
/**
 * Valores del tema, por nombre de columna. Una clave ausente no se toca; null
 * hereda de la fila 0 (ISOFT). El contraste se controla acá sólo entre pares que
 * vienen completos; el servidor lo repite sobre el tema efectivo.
 */
export const webchatTemaValoresSchema = z
    .object(formaTema)
    .strict()
    .superRefine((valores, ctx) => {
    for (const p of problemasDeContraste(valores)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [p.texto],
            message: `Contraste ${p.ratio}:1 (mínimo ${p.minimo}:1); sugerencia ${p.sugerencia}`,
            params: { contraste: p },
        });
    }
});
export const webchatTemaPutSchema = z.object({
    /** FECHAMODIFICACION de la fila del canal, o null si el canal todavía hereda de la fila 0. */
    version: versionSchema.nullable(),
    valores: webchatTemaValoresSchema,
});
/* ── Atajos ───────────────────────────────────────────────────────────── */
export const atajoSchema = z
    .object({
    orden: z.number().int().min(1).max(6),
    etiqueta: textoPlano(24),
    icono: z.enum(ICONOS_WEBCHAT),
    iconoTrazo: z.string().trim().regex(RE_ICONO_TRAZO, "Trazo SVG (atributo d) de hasta 600 caracteres").nullable().optional(),
    colorFondo: colorSchema.nullable().optional(),
    colorIcono: colorSchema.nullable().optional(),
    accion: z.enum(["texto", "url", "asesor"]),
    valor: z.string().trim().max(500).default(""),
})
    .superRefine((a, ctx) => {
    if (a.accion === "texto") {
        const r = textoPlano(300).safeParse(a.valor);
        if (!r.success)
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["valor"], message: "Frase de 1 a 300 caracteres, texto plano" });
    }
    else if (a.accion === "url") {
        if (!urlHttpsValida(a.valor))
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["valor"], message: "Dirección https con nombre de host" });
    }
    else if (a.valor !== "") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["valor"], message: "El atajo al asesor no lleva valor" });
    }
});
function ordenesUnicos(a) {
    return new Set(a.map((x) => x.orden)).size === a.length;
}
export const webchatAtajosPutSchema = z.object({
    /**
     * La FECHAMODIFICACION más nueva de los atajos del canal (también los
     * inactivos), o null si el canal no tiene ninguno: el token de concurrencia.
     */
    version: versionSchema.nullable(),
    atajos: z.array(atajoSchema).max(6).refine(ordenesUnicos, "Cada atajo con un orden distinto"),
});
/** En el alta, los atajos van de 3 a 6: un webchat nuevo nunca nace sin accesos. */
export const MIN_ATAJOS_ALTA = 3;
export const MAX_ATAJOS = 6;
/* ── Alta ─────────────────────────────────────────────────────────────── */
const nombresMarca = {
    nombreMarca: textoPlano(60, { utf8mb3: true }),
    nombreCorto: textoPlano(30).nullable().optional(),
    nombreAsistente: textoPlano(40).nullable().optional(),
    /**
     * La paleta, las fuentes y las formas elegidas en el alta, por columna. Pisan
     * a la base (fila 0 o el webchat de origen); NOMBRE_MARCA sale siempre de
     * nombreMarca y un null no cambia nada (en el alta el tema nace completo).
     */
    valores: webchatTemaValoresSchema.optional(),
};
export const temaAltaSchema = z.discriminatedUnion("origen", [
    z.object({
        origen: z.literal("defecto"),
        ...nombresMarca,
        colorPrimario: colorSchema.nullable().optional(),
    }),
    z.object({
        origen: z.literal("copiar"),
        idcanalOrigen: idcanalSchema,
        copiarAtajos: z.boolean().default(true),
        ...nombresMarca,
    }),
]);
export const webchatAltaSchema = z
    .object({
    /** uuid que genera el cliente: el mismo pedido repetido no crea dos webchats. */
    idempotencyKey: z.string().uuid(),
    idcompany: z.number().int().positive(),
    etiqueta: etiquetaSchema,
    nombre: textoPlano(50, { min: 3, utf8mb3: true }),
    alias: textoPlano(80).nullable().optional(),
    idskill: z.number().int().positive(),
    idbot: z.number().int().min(0).default(0),
    confirmarBotNoVerificado: z.boolean().default(false),
    namebot: z.enum(NAMEBOT_CLAVES).default("masterbot-wp"),
    identidad: identidadEnum,
    paisDefecto: paisSchema,
    paisesPermitidos: paisesPermitidosSchema,
    zonaIana: zonaIanaSchema,
    otp: otpSchema.nullable(),
    tema: temaAltaSchema,
    // Sin link: un webchat nuevo no atiende POST /api/link (no tiene LINK_API_KEY_ENV) y el link lo
    // sigue armando el bot del WhatsApp (requestlink). Nace con LINK_ENVIO 'no'.
    /** De 3 a 6. Sin ellos, los del webchat de origen (si se copia) o ninguno. */
    atajos: z
        .array(atajoSchema)
        .min(MIN_ATAJOS_ALTA, `De ${MIN_ATAJOS_ALTA} a ${MAX_ATAJOS} atajos`)
        .max(MAX_ATAJOS, `De ${MIN_ATAJOS_ALTA} a ${MAX_ATAJOS} atajos`)
        .refine(ordenesUnicos, "Cada atajo con un orden distinto")
        .optional(),
})
    .superRefine((a, ctx) => {
    if (!identidadCoherente(a.identidad, a.otp)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["otp"], message: "Con identidad por OTP hace falta el emisor y la plantilla" });
    }
    if (!paisesCoherentes(a.paisDefecto, a.paisesPermitidos)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["paisesPermitidos"], message: "El país por defecto tiene que estar entre los permitidos" });
    }
});
/* ── Recursos, estado, baja ───────────────────────────────────────────── */
export const ranuraEnum = z.enum(RANURAS_SUBIBLES);
export const motivoSchema = z
    .string()
    .trim()
    .min(3, "Escribí el motivo")
    .max(500)
    .refine((s) => !tieneControles(s, true), "Sin caracteres de control");
export const motivoBodySchema = z.object({ motivo: motivoSchema });
export const bajaSchema = z.object({
    motivo: motivoSchema,
    /** El host escrito a mano, para confirmar. */
    confirmarHost: z.string().trim().toLowerCase(),
    idempotencyKey: z.string().uuid(),
});
/* ── Parámetros y consultas ───────────────────────────────────────────── */
export const idcanalParamSchema = z.object({ idcanal: idcanalSchema });
export const operacionParamSchema = z.object({ id: z.coerce.number().int().positive() });
export const versionParamSchema = z.object({ idcanal: idcanalSchema, version: z.coerce.number().int().positive() });
export const ranuraParamSchema = z.object({ idcanal: idcanalSchema, ranura: ranuraEnum });
/**
 * El listado. page y pageSize son enteros finitos y con tope: Infinity es 400
 * (por .int()), y un page como 1e21 también, porque llegaba al OFFSET del SQL
 * como 2.5e+22 y daba 500.
 */
export const webchatListQuerySchema = z.object({
    q: z.string().trim().min(1).max(120).optional(),
    idcompany: z.coerce.number().int().positive().optional(),
    estado: z.enum(ESTADOS_WEBCHAT).optional(),
    page: z.coerce.number().int().min(1).max(10_000).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const etiquetaQuerySchema = z.object({ etiqueta: z.string().trim().max(63) });
export const catalogosQuerySchema = z.object({ idcompany: z.coerce.number().int().positive().optional() });
export const detalleQuerySchema = z.object({ fresco: z.enum(["0", "1"]).optional() });
//# sourceMappingURL=webchat.js.map